import QRCodeStyling, { DotType, CornerSquareType, Options } from "qr-code-styling";
import { QR_CODE_SIZES, QRCodeDestinationType, QRCodeFileFormat,
  QRCodeSaveParameters, QRCodeSize, QRCodeStylingOptions, QRCodeType } from "../types/QRCode";
import { normalizeUrl, validateUrl } from '../../../constants/helpers';
import { logSafeAxiosError, reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';

export const DEFAULT_QR_SAVE_PARAMETERS: QRCodeSaveParameters = {
  destinationData: {},
  styling: {
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    shape: 'square',
    dotType: 'square',
    hasFrame: false,
    frameTopText: '',
    frameBottomText: '',
    hasLogo: false,
    logoType: 'instagram',
    customLogoUrl: '',
    hasBorder: false,
    borderThickness: 10,
    borderColor: '#000000',
    borderRound: 0,
    borderDasharray: '',
    borderInnerThickness: 0,
    borderInnerColor: '#000000',
    borderOuterThickness: 0,
    borderOuterColor: '#000000',
    topText: '',
    topTextStyle: 'font: 24px sans-serif; fill: #000000;',
    bottomText: '',
    bottomTextStyle: 'font: 24px sans-serif; fill: #000000;',
    leftText: '',
    leftTextStyle: 'font: 24px sans-serif; fill: #000000;',
    rightText: '',
    rightTextStyle: 'font: 24px sans-serif; fill: #000000;',
  },
  previewFormat: 'PNG',
  previewSize: 'Medium',
}

export function getQRCodeSaveParameters(qrCodeParameters: string | null): QRCodeSaveParameters {
  let styling: QRCodeStylingOptions = DEFAULT_QR_SAVE_PARAMETERS.styling;
  let previewFormat: QRCodeFileFormat = DEFAULT_QR_SAVE_PARAMETERS.previewFormat;
  let previewSize: QRCodeSize = DEFAULT_QR_SAVE_PARAMETERS.previewSize;

  // A code generated from a keyword has no parameters of its own: the defaults, not a logged error.
  if (qrCodeParameters == null) {
    return { destinationData: {}, styling, previewFormat, previewSize };
  }

  try {
    const parameters: QRCodeSaveParameters = JSON.parse(qrCodeParameters);
    if (parameters.styling) {
      styling = {
        foregroundColor: parameters.styling.foregroundColor || '#000000',
        backgroundColor: parameters.styling.backgroundColor || '#FFFFFF',
        cornerRadius: parameters.styling.cornerRadius || 0,
        margin: parameters.styling.margin || 10,
        shape: parameters.styling.shape || 'square',
        dotType: parameters.styling.dotType || 'square',
        hasFrame: parameters.styling.hasFrame || false,
        frameTopText: parameters.styling.frameTopText || '',
        frameBottomText: parameters.styling.frameBottomText || '',
        hasLogo: parameters.styling.hasLogo || false,
        logoType: parameters.styling.logoType || 'instagram',
        customLogoUrl: parameters.styling.customLogoUrl || '',
        hasBorder: parameters.styling.hasBorder || false,
        borderThickness: parameters.styling.borderThickness || 20,
        borderColor: parameters.styling.borderColor || '#000000',
        borderRound: parameters.styling.borderRound || 0,
        borderDasharray: parameters.styling.borderDasharray || '',
        borderInnerThickness: parameters.styling.borderInnerThickness || 0,
        borderInnerColor: parameters.styling.borderInnerColor || '#000000',
        borderOuterThickness: parameters.styling.borderOuterThickness || 0,
        borderOuterColor: parameters.styling.borderOuterColor || '#000000',
        topText: parameters.styling.topText || '',
        topTextStyle: parameters.styling.topTextStyle || 'font: 24px sans-serif; fill: #000000;',
        bottomText: parameters.styling.bottomText || '',
        bottomTextStyle:
          parameters.styling.bottomTextStyle || 'font: 24px sans-serif; fill: #000000;',
        leftText: parameters.styling.leftText || '',
        leftTextStyle:
          parameters.styling.leftTextStyle || 'font: 24px sans-serif; fill: #000000;',
        rightText: parameters.styling.rightText || '',
        rightTextStyle:
          parameters.styling.rightTextStyle || 'font: 24px sans-serif; fill: #000000;',
      };
    }
    if (parameters.previewFormat) {
      previewFormat = parameters.previewFormat;
    }
    if (parameters.previewSize) {
      previewSize = parameters.previewSize;
    }
  } catch (error) {
    logSafeAxiosError(error);
  }

  return {
    destinationData: {},
    styling,
    previewFormat,
    previewSize,
  };
}

const validateGoogleReviewUrl = (url: string): boolean => {
  return url.includes('google.com') && (url.includes('review') || url.includes('maps'));
};

export function validateAllFields(formData: QRCodeSaveParameters,
  destinationType: QRCodeDestinationType, name: string): boolean {

  if (!name.trim()) return false;

  const data = formData.destinationData || {};

  switch (destinationType) {
    case 'URL': {
      const url = (data.url as string) || '';
      return url.trim() !== '' && validateUrl(url);
    }

    case 'SMS': {
      const message = (data.message as string) || '';
      return message.trim() !== '';
    }

    case 'Contact Card': {
      const vcardData = (data?.vcardData as string) || '';
      return vcardData.trim() !== '';
    }

    case 'Instagram': {
      const handle = (data.handle as string) || '';
      return handle.replace(/^@/, '').trim() !== '';
    }

    case 'Facebook': {
      const facebookPage = (data.facebookPage as string) || '';
      return facebookPage.trim() !== '';
    }

    case 'Google Review': {
      const googleUrl = (data.url as string) || '';
      return (
        googleUrl.trim() !== '' && validateUrl(googleUrl) && validateGoogleReviewUrl(googleUrl)
      );
    }

    default:
      return false;
  }
}

export const mapDestinationTypeToQRCodeType = (destinationType: QRCodeDestinationType): QRCodeType => {
  switch (destinationType) {
    case 'URL':
      return 'Go to website';
    case 'SMS':
      return 'Compose SMS';
    case 'Contact Card':
      return 'Download file';
    case 'Instagram':
      return 'Visit Instagram';
    case 'Facebook':
      return 'Visit Facebook';
    case 'Google Review':
      return 'Go to Google Reviews Page';
    default:
      return 'Go to website';
  }
};

export const mapQRCodeTypeToDestinationType = (qrCodeType: QRCodeType): QRCodeDestinationType => {
  switch (qrCodeType) {
    case 'Go to website':
      return 'URL';
    case 'Compose SMS':
      return 'SMS';
    case 'Download file':
      return 'Contact Card';
    case 'Visit Instagram':
      return 'Instagram';
    case 'Visit Facebook':
      return 'Facebook';
    case 'Go to Google Reviews Page':
      return 'Google Review';
    default:
      return 'URL';
  }
};

export const getDestinationTypeFromQrData = (
  qrCodeTypeName: string,
  qrData: string,
): QRCodeDestinationType => {
  let destinationType = mapQRCodeTypeToDestinationType(qrCodeTypeName as QRCodeType);

  if (qrData.toLowerCase().startsWith('urlto:')) {
    const url = qrData.substring(6);
    if (url.includes('facebook.com/')) {
      destinationType = 'Facebook';
    } else if (url.includes('instagram.com/')) {
      destinationType = 'Instagram';
    } else if (url.includes('google.com') && (url.includes('review') || url.includes('maps'))) {
      destinationType = 'Google Review';
    }
  }

  return destinationType;
};

export const getDestinationDataFromQrData = (
  qrData: string,
  destinationType: QRCodeDestinationType,
): Record<string, unknown> => {
  if (qrData.toLowerCase().startsWith('smsto:')) {
    const parts = qrData.substring(6).split(':');
    if (parts.length >= 2) {
      return {
        phoneNumber: parts[0],
        message: parts.slice(1).join(':'),
      };
    }
  } else if (qrData.toLowerCase().startsWith('urlto:')) {
    const url = qrData.substring(6);
    if (url.includes('facebook.com/')) {
      const facebookMatch = url.match(/facebook\.com\/([^/?#]+)/);
      return { facebookPage: facebookMatch ? facebookMatch[1] : url };
    }
    if (url.includes('instagram.com/')) {
      const instagramMatch = url.match(/instagram\.com\/([^/?#]+)/);
      return { handle: instagramMatch ? `${instagramMatch[1]}` : url };
    }
    return { url };
  } else if (qrData.startsWith('BEGIN:VCARD')) {
    return { vcardData: qrData };
  } else if (destinationType === 'URL' || destinationType === 'Google Review') {
    return { url: qrData };
  } else if (destinationType === 'Facebook' && qrData.includes('facebook.com/')) {
    const facebookMatch = qrData.match(/facebook\.com\/([^/?#]+)/);
    return { facebookPage: facebookMatch ? facebookMatch[1] : qrData };
  } else if (destinationType === 'Instagram' && qrData.includes('instagram.com/')) {
    const instagramMatch = qrData.match(/instagram\.com\/([^/?#]+)/);
    return { handle: instagramMatch ? `${instagramMatch[1]}` : qrData };
  }

  if (qrData.startsWith('http://') || qrData.startsWith('https://') || qrData.startsWith('www.')) {
    return { url: qrData };
  }
  return { vcardData: qrData };
};

const getDefaultQrData = (destinationType: QRCodeDestinationType): string => {
  switch (destinationType) {
    case 'URL':
      return 'https://example.com';
    case 'SMS':
      return 'smsto:Hello!';
    case 'Contact Card':
      return 'BEGIN:VCARD\nVERSION:3.0\nFN:Sample Contact\nORG:Sample Company\nTEL:+1234567890\nEMAIL:contact@example.com\nEND:VCARD';
    case 'Instagram':
      return 'https://instagram.com/username';
    case 'Facebook':
      return 'https://www.facebook.com/page';
    case 'Google Review':
      return 'https://g.page/r/example';
    default:
      return 'https://example.com';
  }
}

export function getQrData(formData: QRCodeSaveParameters, destinationType: QRCodeDestinationType): string {
  const dt = destinationType;
  const data = formData.destinationData || {};

  if (dt === 'URL') {
    const url = (data.url as string) || '';
    if (!url) return getDefaultQrData(destinationType);
    const normalizedUrl = normalizeUrl(url);
    return `${normalizedUrl}`;
  }

  if (dt === 'SMS') {
    const phone = (data.phoneNumber as string) || '';
    const msg = (data.message as string) || '';
    if (!phone || !msg) return getDefaultQrData(destinationType);
    return `smsto:${phone}:${msg}`;
  }

  if (dt === 'Contact Card') {
    const vcardData = (data.vcardData as string) || '';
    if (vcardData) {
      return vcardData;
    }
    return 'BEGIN:VCARD\nVERSION:3.0\nFN:Sample Contact\nEND:VCARD';
  }

  if (dt === 'Instagram') {
    const handle = ((data.handle as string) || '').replace(/^@/, '');
    if (!handle) return getDefaultQrData(destinationType);
    const url = `https://instagram.com/${handle}`;
    return url;
  }

  if (dt === 'Facebook') {
    const page = (data.facebookPage as string) || '';
    if (!page) return getDefaultQrData(destinationType);
    const url = `https://www.facebook.com/${page}`;
    return url;
  }

  if (dt === 'Google Review') {
    const url = (data.url as string) || '';
    if (!url) return getDefaultQrData(destinationType);
    const normalizedUrl = normalizeUrl(url);
    return normalizedUrl;
  }

  return getDefaultQrData(destinationType);
}

export function getLogoUrl(styling: QRCodeStylingOptions): string | undefined {
  if (!styling.hasLogo) return undefined;

  if (styling.logoType === 'custom' && styling.customLogoUrl) {
    return styling.customLogoUrl;
  }

  const fillColor = styling.foregroundColor;

  const instagramSvg = (fillColor: string) => {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="20" height="20" rx="6" fill="${fillColor}"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
      <circle cx="12" cy="12" r="2.5" fill="${fillColor}"/>
      <circle cx="17.5" cy="6.5" r="1.5" fill="white"/>
    </svg>`;
  }
  const facebookSvg = (fillColor: string) => {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="${fillColor}"/>
      <path d="M15.5 8.5h-2c-0.5 0-1 0.5-1 1v1.5h3l-0.5 2.5h-2.5v6h-2.5v-6h-1.5v-2.5h1.5v-1.5c0-2 1.5-3.5 3.5-3.5h2v2.5z" fill="white"/>
    </svg>`;
  }
  const googleSvg = (fillColor: string) => {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 12.2h-5.5v2.2h3.2c-.4 1.3-1.6 2.3-3.2 2.3-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5c1 0 1.9.4 2.5 1l1.6-1.6c-1-1-2.5-1.6-4.1-1.6-3.2 0-5.8 2.6-5.8 5.8s2.6 5.8 5.8 5.8c3.3 0 5.5-2.3 5.5-5.7 0-.4 0-.8-.1-1.2z" fill="${fillColor}"/>
    </svg>`;
  }

  switch (styling.logoType) {
    case 'instagram':
      return 'data:image/svg+xml;base64,' + btoa(instagramSvg(fillColor));
    case 'facebook':
      return 'data:image/svg+xml;base64,' + btoa(facebookSvg(fillColor));
    case 'google':
      return 'data:image/svg+xml;base64,' + btoa(googleSvg(fillColor));
    default:
      return undefined;
  }
}

export function getDotType(type: string | undefined): DotType {
  if (!type) return 'square';
  if (type === 'circle') return 'dots';
  if (type === 'rounded') return 'rounded';
  return 'square';
}

export function getCornerType(type: string | undefined): CornerSquareType {
  if (!type) return 'square';
  if (type === 'circle') return 'dot';
  if (type === 'rounded') return 'extra-rounded';
  return 'square';
}

export async function renderQrCodePreview({
  container,
  data,
  size,
  styling,
  margin = 5,
}: {
  container: HTMLDivElement;
  data: string;
  size: number;
  styling: QRCodeStylingOptions;
  destinationType: QRCodeDestinationType;
  margin?: number;
}): Promise<QRCodeStyling> {
  container.innerHTML = '';

  const dotType = getDotType(styling.dotType);
  const cornerType = getCornerType(styling.dotType);
  const isCircular = styling.shape === 'circle';
  const shape = isCircular ? 'circle' : 'square';
  const logoUrl = getLogoUrl(styling);

  const instance: QRCodeStyling = new QRCodeStyling({
    width: size,
    height: size,
    type: 'svg',
    data,
    shape,
    margin,
    qrOptions: {},
    dotsOptions: {
      color: styling.foregroundColor,
      type: dotType,
    },
    cornersSquareOptions: {
      color: styling.foregroundColor,
      type: cornerType,
    },
    cornersDotOptions: {
      color: styling.foregroundColor,
      type: dotType === 'dots' ? 'dot' : 'square',
    },
    backgroundOptions: {
      color: styling.backgroundColor || '#FFFFFF',
    },
    ...(logoUrl
      ? {
          image: logoUrl,
          imageOptions: {
            hideBackgroundDots: true,
            imageSize: 0.3,
            margin: 10,
            crossOrigin: 'anonymous',
          },
        }
      : {}),
  });

  const tempDiv = document.createElement('div');
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.position = 'absolute';
  instance.append(tempDiv);

  const waitForRender = () =>
    new Promise<void>(resolve => {
      if (logoUrl) {
        setTimeout(() => resolve(), 100);
      } else {
        resolve();
      }
    });

  await waitForRender();

  const qrSvgElement = tempDiv.querySelector('svg');
  if (styling.hasBorder && qrSvgElement) {
    const borderedSvg = createBorderWithText(
      qrSvgElement.outerHTML,
      size,
      isCircular,
      styling.topText || '',
      styling.bottomText || '',
      styling.foregroundColor || '#000000',
    );
    container.innerHTML = borderedSvg;
  } else {
    container.innerHTML = tempDiv.innerHTML;
  }

  return instance;
}

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export function createBorderWithText(
  qrSvg: string,
  width: number,
  isCircular: boolean,
  topText: string,
  bottomText: string,
  borderColor: string,
): string {
  const widthMatch = qrSvg.match(/width="([^"]*)"/);
  const heightMatch = qrSvg.match(/height="([^"]*)"/);
  const viewBoxMatch = qrSvg.match(/viewBox="([^"]*)"/);

  let qrWidth = width;
  let qrHeight = width;

  if (widthMatch && heightMatch) {
    qrWidth = parseFloat(widthMatch[1]);
    qrHeight = parseFloat(heightMatch[1]);
  } else if (viewBoxMatch) {
    const viewBoxValues = viewBoxMatch[1].split(' ');
    if (viewBoxValues.length >= 4) {
      qrWidth = parseFloat(viewBoxValues[2]);
      qrHeight = parseFloat(viewBoxValues[3]);
    }
  }

  const maxDimension = Math.max(qrWidth, qrHeight);

  const hasText = topText || bottomText;
  const borderWidth = hasText ? 20 : 8;
  const totalSize = maxDimension + borderWidth * 2;
  const center = totalSize / 2;

  const escapedTopText = escapeXml(topText);
  const escapedBottomText = escapeXml(bottomText);

  let borderPath = '';
  let topTextElement = '';
  let bottomTextElement = '';

  if (isCircular) {
    let gap = 0.85;
    switch (width) {
      case 200:
        gap = 0.85;
        break;
      case 600:
        gap = 0.86;
        break;
      case 1200 :
        gap = 0.87;
        break;
      default:
        gap = 0.85;
        break;
    }
    const qrCircleRadius = (maxDimension / 2) * gap;
    // Make gap proportional to QR code size for consistent appearance across all sizes
    // Use 3% of the max dimension as gap (this gives ~18px for 600px, ~36px for 1200px, ~6px for 200px)
    const gapWidth = maxDimension * 0.03;
    const innerRadius = qrCircleRadius + gapWidth;
    const strokeRadius = innerRadius + borderWidth / 2;

    borderPath = `<circle cx="${center}" cy="${center}" r="${strokeRadius}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" />`;

    const textRadius = innerRadius + borderWidth / 4;

    if (topText) {
      const pathId = `topPath_${Math.random().toString(36).substr(2, 9)}`;
      const startX = center - textRadius;
      const endX = center + textRadius;
      topTextElement = `<defs><path id="${pathId}" d="M ${startX} ${center} A ${textRadius} ${textRadius} 0 0 1 ${endX} ${center}" fill="none"/></defs><text font-family="sans-serif" font-size="16" fill="white" font-weight="bold"><textPath href="#${pathId}" startOffset="50%" text-anchor="middle">${escapedTopText}</textPath></text>`;
    }

    if (bottomText) {
      const pathId = `bottomPath_${Math.random().toString(36).substr(2, 9)}`;
      const bottomTextRadius = textRadius + 10;
      const bottomStartX = center - bottomTextRadius;
      const bottomEndX = center + bottomTextRadius;
      const bottomY = center;
      bottomTextElement = `<defs><path id="${pathId}" d="M ${bottomStartX} ${bottomY} A ${bottomTextRadius} ${bottomTextRadius} 0 1 0 ${bottomEndX} ${bottomY}" fill="none"/></defs><text font-family="sans-serif" font-size="16" fill="white" font-weight="bold"><textPath href="#${pathId}" startOffset="50%" text-anchor="middle">${escapedBottomText}</textPath></text>`;
    }
  } else {
    const rectOffset = borderWidth / 2;
    const rectSize = maxDimension + borderWidth;
    borderPath = `<rect x="${rectOffset}" y="${rectOffset}" width="${rectSize}" height="${rectSize}" fill="none" stroke="${borderColor}" stroke-width="${borderWidth}" rx="4" />`;

    if (topText) {
      topTextElement = `<text x="${center}" y="${rectOffset}" font-family="sans-serif" font-size="14" fill="white" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${escapedTopText}</text>`;
    }

    if (bottomText) {
      bottomTextElement = `<text x="${center}" y="${
        totalSize - rectOffset
      }" font-family="sans-serif" font-size="14" fill="white" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${escapedBottomText}</text>`;
    }
  }

  const qrOffsetX = (totalSize - qrWidth) / 2;
  const qrOffsetY = (totalSize - qrHeight) / 2;

  const wrappedSvg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${totalSize}" height="${totalSize}" viewBox="0 0 ${totalSize} ${totalSize}"><g transform="translate(${qrOffsetX}, ${qrOffsetY})">${qrSvg
    .replace(/<svg[^>]*>/, '')
    .replace(/<\/svg>/, '')}</g>${borderPath}${topTextElement}${bottomTextElement}</svg>`;

  return wrappedSvg;
}

export async function handleDownload(formData: QRCodeSaveParameters,
  destinationType: QRCodeDestinationType, name: string, currentCompanyName: string,
  data: string): Promise<void> {

  if (!validateAllFields(formData, destinationType, name)) {
    console.error('Validation failed - missing or invalid required fields for download');
    return;
  }

  try {
    const qrContainerRef: { current: HTMLDivElement | null } = {
      current: document.createElement('div'),
    };

    const qrData = data;
    if (!qrData) {
      console.error('Cannot generate QR data for download');
      return;
    }

    const sizeConfig = QR_CODE_SIZES[formData.previewSize];
    const downloadWidth = sizeConfig.width;
    const downloadHeight = sizeConfig.height;

    const dotType = getDotType(formData.styling.dotType);
    const cornerType = getCornerType(formData.styling.dotType);
    const isCircular = formData.styling.shape === 'circle';
    const shape = isCircular ? 'circle' : 'square';
    const logoUrl = getLogoUrl(formData.styling);

    const options: Options = {
      width: downloadWidth,
      height: downloadHeight,
      type: 'svg',
      data: qrData,
      shape,
      margin: Math.floor(downloadWidth * 0.018),
      qrOptions: {},
      dotsOptions: {
        color: formData.styling.foregroundColor,
        type: dotType,
      },
      cornersSquareOptions: {
        color: formData.styling.foregroundColor,
        type: cornerType,
      },
      cornersDotOptions: {
        color: formData.styling.foregroundColor,
        type: dotType === 'dots' ? 'dot' : 'square',
      },
      backgroundOptions: {
        color: formData.styling.backgroundColor,
      },
      image: logoUrl,
      imageOptions: logoUrl
        ? {
            hideBackgroundDots: true,
            imageSize: 0.25,
            margin: 10,
            crossOrigin: 'anonymous',
          }
        : {},
    };
    const downloadQrInstance = new QRCodeStyling(options);

    const companyNamePrefix = currentCompanyName
      .replace(/\s*-\s*/g, '-')
      .replace(/\s+/g, '-').toLowerCase();
    const qrCodeName = name.replace(/\s+/g, '-');
    const fileName = `${companyNamePrefix}-qr-code-${qrCodeName}`;
    const extension = formData.previewFormat.toLowerCase() as 'png' | 'jpg' | 'svg';
    const showBorder = formData.styling.hasBorder;

    if (showBorder) {
      const tempDiv = document.createElement('div');
      downloadQrInstance.append(tempDiv);

      const waitForRender = () =>
        new Promise<void>(resolve => {
          if (logoUrl) {
            setTimeout(() => resolve(), 100);
          } else {
            resolve();
          }
        });

      await waitForRender();

      const qrSvgElement = tempDiv.querySelector('svg');
      if (!qrSvgElement) {
        console.error('No SVG found to build border');
        return;
      }

      const qrSvgString = qrSvgElement.outerHTML;
      const borderedSvg = createBorderWithText(
        qrSvgString,
        downloadWidth,
        isCircular,
        formData.styling.topText || '',
        formData.styling.bottomText || '',
        formData.styling.foregroundColor || '#000000',
      );

      if (qrContainerRef.current) {
        qrContainerRef.current.innerHTML = borderedSvg;
      }

      const svgElement = qrContainerRef.current?.querySelector('svg') as SVGSVGElement | null;
      if (!svgElement) {
        console.error('No SVG found in bordered output');
        return;
      }

      const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
      clonedSvg.setAttribute('width', downloadWidth.toString());
      clonedSvg.setAttribute('height', downloadHeight.toString());
      const svgString = clonedSvg.outerHTML;

      if (extension === 'svg') {
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
          const svgUrl = URL.createObjectURL(svgBlob);

          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = downloadWidth;
              canvas.height = downloadHeight;
              const ctx = canvas.getContext('2d');

              if (!ctx) {
                URL.revokeObjectURL(svgUrl);
                reject(new Error('Failed to get canvas context'));
                return;
              }

              if (extension === 'jpg') {
                ctx.fillStyle = '#FFFFFF';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
              }

              ctx.drawImage(img, 0, 0, downloadWidth, downloadHeight);

              canvas.toBlob(
                blob => {
                  URL.revokeObjectURL(svgUrl);

                  if (!blob) {
                    reject(new Error('Failed to create blob from canvas'));
                    return;
                  }

                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `${fileName}.${extension}`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  resolve();
                },
                `image/${extension === 'jpg' ? 'jpeg' : 'png'}`,
                1.0,
              );
            } catch (err) {
              URL.revokeObjectURL(svgUrl);
              reject(err);
            }
          };

          img.onerror = e => {
            logSafeAxiosError(e);
            URL.revokeObjectURL(svgUrl);
            reject(new Error('Failed to load SVG as image'));
          };

          img.src = svgUrl;
        });
      }
    } else {
      (
        downloadQrInstance as unknown as {
          download: (options: { name: string; extension: string }) => void;
        }
      ).download({
        name: fileName,
        extension: extension === 'jpg' ? 'jpeg' : extension,
      });
    }
  } catch (error) {
    reportErrorToUser(error, GENERIC_ERROR_MESSAGE);
  }
}
